import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { IColumn } from '@feathr/components';
import { Pill, TableColumnHeader, Text, Time } from '@feathr/components';
import { TimeFormat } from '@feathr/hooks';

import type { ISampleTemplate } from '../sampleData';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as shellStyles from '../Shell/PrototypeShell.css';

export type TTemplateRowAction = 'edit' | 'duplicate' | 'rename' | 'delete';

interface ITemplateColumnsArgs {
  base: string;
  onAction: (template: ISampleTemplate, action: TTemplateRowAction) => void;
  renderActions: (
    template: ISampleTemplate,
    onAction: (t: ISampleTemplate, a: TTemplateRowAction) => void,
  ) => JSX.Element;
  t: TFunction;
}

function templateColumns({
  base,
  onAction,
  renderActions,
  t,
}: ITemplateColumnsArgs): Array<IColumn<ISampleTemplate>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.name}</Link>;
      },
    },
    {
      accessor: 'type',
      id: 'type',
      Header: TableColumnHeader({ title: t('Type'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 140,
      Cell({ original }): JSX.Element {
        return <Pill theme={'neutral'}>{original.type}</Pill>;
      },
    },
    {
      accessor: 'group',
      id: 'group',
      Header: TableColumnHeader({ title: t('Group'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 180,
      Cell({ original }): JSX.Element {
        return <Text variant={'smallRegular'}>{original.group}</Text>;
      },
    },
    {
      accessor: 'lastUpdated',
      id: 'lastUpdated',
      Header: TableColumnHeader({ title: t('Last updated'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 160,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.shortDate} timestamp={original.lastUpdated} />;
      },
    },
    {
      id: 'actions',
      Header: TableColumnHeader({ title: '' }),
      width: 56,
      sortable: false,
      className: `${tableStyles.cellCenter} ${shellStyles.actionsCell}`,
      Cell({ original }): JSX.Element {
        return renderActions(original, onAction);
      },
    },
  ];
}

export default templateColumns;
